import Cookies from "js-cookie";
import { getI18n } from "react-i18next";

import { APIError, ResponseParseError } from "./errors";
import { generateRequestId } from "./generateRequestId";

export type SubmitErrorInterceptor = <Data>(
  error: APIError,
  retry: (body?: unknown) => Promise<Data>,
  context: { path: string; body: unknown; init: RequestInit }
) => Promise<Data>;

export type APIErrorListenerContext = {
  path: string;
  method: string;
};

export function normalizeAPIRequestPath(
  pathOrUrl: string,
  baseUrl?: string
): string {
  if (!pathOrUrl) {
    return pathOrUrl;
  }

  try {
    const base = baseUrl && baseUrl.length > 0 ? baseUrl : "http://localhost";
    const url = pathOrUrl.startsWith("http")
      ? new URL(pathOrUrl)
      : new URL(pathOrUrl, base.endsWith("/") ? base : `${base}/`);

    return url.pathname;
  } catch {
    const withoutQuery = pathOrUrl.split("?")[0]?.split("#")[0] ?? pathOrUrl;
    return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  }
}

export type APIErrorListener = (
  error: APIError,
  context: APIErrorListenerContext
) => void;

function baseRequestHeaders(requestId: string) {
  return {
    "X-CSRFToken": Cookies.get("csrftoken") || "",
    "X-Request-Id": requestId,
  };
}

export async function JSONRequest<Data extends unknown | undefined>(
  url: string,
  init: RequestInit = { method: "GET" }
) {
  const requestId = generateRequestId();

  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...baseRequestHeaders(requestId),
      ...init.headers,
    },
  });

  const text = await response.text();
  let body: unknown;

  try {
    body = text ? JSON.parse(text) : undefined;
  } catch (e) {
    throw new ResponseParseError({
      status: response.status,
      requestId: response.headers.get("X-Request-Id") ?? requestId,
      cause: e,
    });
  }

  if (!response.ok) {
    throw new APIError(response.status, response.statusText, body);
  }

  return body as Data;
}

class Fetcher {
  public getAccessToken = async () => "";

  public domain?: string = "";

  public onSubmitError: SubmitErrorInterceptor | null = null;

  public tagAPIErrorBeforeNotify: ((error: APIError) => void) | null = null;

  public getBootstrapEpochForRequest: (() => number | undefined) | null = null;

  private apiErrorListeners = new Set<APIErrorListener>();

  public addAPIErrorListener(listener: APIErrorListener): () => void {
    this.apiErrorListeners.add(listener);
    return () => {
      this.apiErrorListeners.delete(listener);
    };
  }

  private notifyAPIErrorListeners(
    error: APIError,
    context: APIErrorListenerContext
  ) {
    this.tagAPIErrorBeforeNotify?.(error);
    for (const listener of this.apiErrorListeners) {
      listener(error, context);
    }
  }

  private async prepareAuthorizedRequest(path: string, domain?: string) {
    if (!domain) {
      throw new Error(
        "Client not initialised. Are you missing an APIProvider?"
      );
    }

    let token = "";
    try {
      token = await this.getAccessToken();
    } catch {
      // likely refresh token is invalid
    }

    return {
      url: new URL(path, domain).toString(),
      authHeaders: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": getI18n()?.language,
      },
    };
  }

  private reportAPIError(
    error: APIError,
    {
      path,
      method,
      domain,
      bootstrapEpoch,
    }: {
      path: string;
      method: string;
      domain?: string;
      bootstrapEpoch?: number;
    }
  ) {
    if (bootstrapEpoch !== undefined) {
      (error as APIError & { bootstrapEpoch?: number }).bootstrapEpoch =
        bootstrapEpoch;
    }
    this.notifyAPIErrorListeners(error, {
      path: normalizeAPIRequestPath(path, domain),
      method: method.toUpperCase(),
    });
  }

  public requestBlob = async (
    path: string,
    init: RequestInit = { method: "GET" },
    domain = this.domain
  ): Promise<Blob> => {
    const requestBootstrapEpoch = this.getBootstrapEpochForRequest?.();
    const method = init.method ?? "GET";
    const { url, authHeaders } = await this.prepareAuthorizedRequest(
      path,
      domain
    );

    const response = await fetch(url, {
      credentials: "same-origin",
      ...init,
      headers: {
        Accept: "*/*",
        ...baseRequestHeaders(generateRequestId()),
        ...init.headers,
        ...authHeaders,
      },
    });

    if (!response.ok) {
      const text = await response.text();
      let body: unknown;
      try {
        body = text ? JSON.parse(text) : undefined;
      } catch {
        body = text || undefined;
      }
      const error = new APIError(response.status, response.statusText, body);
      this.reportAPIError(error, {
        path,
        method,
        domain,
        bootstrapEpoch: requestBootstrapEpoch,
      });
      throw error;
    }

    return response.blob();
  };

  public request = async <Data>(
    path: string,
    init: RequestInit = { method: "GET" },
    domain = this.domain
  ): Promise<Data> => {
    const requestBootstrapEpoch = this.getBootstrapEpochForRequest?.();
    const method = init.method ?? "GET";
    const { url, authHeaders } = await this.prepareAuthorizedRequest(
      path,
      domain
    );

    try {
      return await JSONRequest<Data>(url, {
        ...init,
        headers: {
          ...init.headers,
          ...authHeaders,
        },
      });
    } catch (error) {
      if (error instanceof APIError) {
        this.reportAPIError(error, {
          path,
          method,
          domain,
          bootstrapEpoch: requestBootstrapEpoch,
        });
      }
      throw error;
    }
  };

  public submit = async <Data>(
    path: string,
    body: unknown = {},
    init: RequestInit = {}
  ): Promise<Data> => {
    const method = init.method ?? "POST";

    try {
      return await this.request<Data>(path, {
        method,
        body: JSON.stringify(body),
        ...init,
      });
    } catch (error) {
      if (this.onSubmitError && error instanceof APIError) {
        const retry = <D>(retryBody?: unknown) =>
          this.request<D>(path, {
            method,
            body: JSON.stringify(retryBody ?? body),
            ...init,
          });
        return this.onSubmitError<Data>(error, retry, { path, body, init });
      }
      throw error;
    }
  };

  public initialize(params: {
    domain?: string;
    getAccessToken: () => Promise<string>;
  }) {
    this.domain = params.domain;
    this.getAccessToken = params.getAccessToken;
  }
}

export const fetcher = new Fetcher();
